import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import Pagination from '../../components/ui/Pagination';
import FilterTabs from '../../components/ui/FilterTabs';
import CopyButton from '../../components/ui/CopyButton';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/PanelCard';
import UserDetailModal from './UserDetailModal';

const STATUS_FILTERS = [
  { id: '',          label: 'All' },
  { id: 'pending',   label: 'Pending' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'expired',   label: 'Expired' },
];

const PAYMENT_FILTERS = [
  { id: '',       label: 'All methods' },
  { id: 'crypto', label: 'Crypto (LTC)' },
  { id: 'card',   label: 'Card' },
];

function statusBadge(status) {
  const cfg = {
    delivered: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25',
    pending:   'bg-amber-500/12 text-amber-300 border-amber-500/25',
    expired:   'bg-white/[0.05] text-gray-500 border-white/[0.08]',
  }[status] || 'bg-white/[0.05] text-gray-400 border-white/[0.08]';
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize w-fit ${cfg}`}>
      {status}
    </span>
  );
}

/**
 * Rövidített, másolható érték (kulcs, LTC cím, Whop session).
 * A korábbi verzió a toast context-et függvényként hívta, ami hibát dobott —
 * a közös CopyButton saját visszajelzést ad, így nincs rá szükség.
 */
function CopyValue({ value }) {
  return (
    <span
      title={value}
      className="inline-flex items-center gap-1 max-w-[180px] rounded-md px-1.5 py-0.5
                 bg-black/30 border border-white/[0.07]"
    >
      <span className="font-mono text-xs text-brand-200 truncate">{value}</span>
      <CopyButton value={value} variant="icon" className="w-5 h-5" />
    </span>
  );
}

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
}

export default function OrdersTab() {
  const [orders, setOrders]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('');
  const [payFilter, setPay]       = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      if (payFilter)    params.set('payment', payFilter);
      const res = await api.get(`/owner/orders?${params}`);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, payFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const changeStatus = (f) => { setStatus(f); setPage(1); };
  const changePay    = (f) => { setPay(f);    setPage(1); };

  return (
    <>
    {selectedUserId && (
      <UserDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
    )}
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-4 space-y-3">
        <FilterTabs filters={STATUS_FILTERS}  current={statusFilter} onChange={changeStatus} />
        <FilterTabs filters={PAYMENT_FILTERS} current={payFilter}    onChange={changePay} />
      </div>

      {/* Table */}
      <div className="card scroll-x">
        {loading ? (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon={<><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18M16 10a4 4 0 01-8 0" /></>}
            title="No orders found."
          />
        ) : (
          <>
          {/* Mobilon kártyák: a kilenc oszlopos tábla 375 px-en ~900 px oldalra
              görgetést kért — a kulcs és az LTC-cím a görgetés legvégén volt. */}
          <div className="md:hidden divide-y divide-white/[0.05]">
            {orders.map(o => (
              <div key={o.id} className="px-4 py-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-gray-500">#{o.id}</span>
                  <button onClick={() => setSelectedUserId(o.user_id)}
                          className="text-white font-medium hover:text-brand-200 transition-colors text-left">
                    {o.buyer_name}
                  </button>
                  <span className="ml-auto">{statusBadge(o.status)}</span>
                </div>
                <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                  <span className="font-mono">{fmt(o.created_at)}</span>
                  <span>{o.product_name} · {o.plan_label}</span>
                  <span className={o.payment_method === 'crypto' ? 'text-sky-300' : 'text-brand-200'}>
                    {o.payment_method === 'crypto' ? 'LTC' : 'Card'}
                  </span>
                  <span className="text-gray-200 tabular-nums">
                    {o.amount_paid != null && parseFloat(o.amount_paid) !== parseFloat(o.final_price) ? (
                      <>
                        <span className="text-gray-600 line-through mr-1">€{parseFloat(o.final_price).toFixed(2)}</span>
                        <span className="text-emerald-400">€{parseFloat(o.amount_paid).toFixed(2)}</span>
                      </>
                    ) : <>€{parseFloat(o.final_price).toFixed(2)}</>}
                  </span>
                  {o.promo_code && <span className="font-mono text-emerald-400/80">{o.promo_code}</span>}
                  {o.payment_method === 'crypto' && o.ltc_amount && (
                    <span>{parseFloat(o.ltc_amount).toFixed(6)} LTC</span>
                  )}
                  {o.status === 'pending' && o.payment_method === 'crypto' && o.ltc_confirmations != null && (
                    <span>{o.ltc_confirmations}/3 conf.</span>
                  )}
                  {o.delivered_at && <span>delivered {fmt(o.delivered_at)}</span>}
                </div>
                {(o.license_key || (o.payment_method === 'crypto' && o.ltc_address) || o.whop_session_id) && (
                  <div className="mt-2 flex flex-col gap-1.5 items-start text-xs">
                    {o.license_key && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <CopyValue value={o.license_key} />
                        {o.key_is_used
                          ? <span className="text-emerald-400">
                              Activated{o.key_used_by ? ' by ' + o.key_used_by : ''}
                              {o.key_used_at && <span className="text-gray-600"> · {fmt(o.key_used_at)}</span>}
                            </span>
                          : <span className="text-gray-600">Not activated yet</span>}
                      </div>
                    )}
                    {o.payment_method === 'crypto' && o.ltc_address && <CopyValue value={o.ltc_address} />}
                    {o.payment_method === 'card' && o.whop_session_id && <CopyValue value={o.whop_session_id} />}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="hidden md:block">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.03]">
                {['#', 'Date', 'Buyer', 'Plan', 'Method', 'Amount', 'Status', 'Key & Activation', 'LTC address'].map(h => (
                  <th key={h} className="text-left px-4 py-3 table-head whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="table-row last:border-0">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-gray-500">#{o.id}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs font-mono">
                    {fmt(o.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedUserId(o.user_id)}
                      className="text-white font-medium hover:text-brand-200 transition-colors text-left"
                    >
                      {o.buyer_name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    <span>{o.product_name}</span>
                    <span className="text-gray-700 mx-1.5">·</span>
                    <span className="text-gray-600 text-xs">{o.plan_label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {o.payment_method === 'crypto' ? (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border
                                       bg-sky-500/12 text-sky-300 border-sky-500/25">LTC</span>
                    ) : (
                      <div className="flex flex-col gap-1.5 items-start">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border
                                         bg-brand-500/12 text-brand-200 border-brand-400/25">Card</span>
                        {o.whop_session_id && <CopyValue value={o.whop_session_id} />}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 whitespace-nowrap tabular-nums">
                    {/* Kuponnal a listaár áthúzva, a ténylegesen fizetett összeg a fő szám */}
                    {o.amount_paid != null && parseFloat(o.amount_paid) !== parseFloat(o.final_price) ? (
                      <>
                        <span className="text-gray-600 line-through mr-1.5">€{parseFloat(o.final_price).toFixed(2)}</span>
                        <span className="text-emerald-400">€{parseFloat(o.amount_paid).toFixed(2)}</span>
                      </>
                    ) : (
                      <>€{parseFloat(o.final_price).toFixed(2)}</>
                    )}
                    {o.promo_code && (
                      <span className="block text-xs text-emerald-400/80 font-mono mt-0.5">{o.promo_code}</span>
                    )}
                    {o.payment_method === 'crypto' && o.ltc_amount && (
                      <span className="block text-xs text-gray-600 mt-0.5">
                        {parseFloat(o.ltc_amount).toFixed(6)} LTC
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {statusBadge(o.status)}
                      {o.status === 'pending' && o.payment_method === 'crypto' && o.ltc_confirmations != null && (
                        <span className="text-xs text-gray-600">{o.ltc_confirmations}/3 conf.</span>
                      )}
                      {o.delivered_at && (
                        <span className="text-xs text-gray-600">{fmt(o.delivered_at)}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {o.license_key ? (
                      <div className="flex flex-col gap-1.5 items-start">
                        <CopyValue value={o.license_key} />
                        {o.key_is_used ? (
                          <span className="text-xs text-emerald-400 flex items-center gap-1 flex-wrap">
                            Activated
                            {o.key_used_by && (
                              <button
                                onClick={() => setSelectedUserId(o.user_id)}
                                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2 transition-colors"
                              >
                                by {o.key_used_by}
                              </button>
                            )}
                            {o.key_used_at && <span className="text-gray-600">· {fmt(o.key_used_at)}</span>}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600">Not activated yet</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-700 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {o.payment_method === 'crypto' && o.ltc_address
                      ? <CopyValue value={o.ltc_address} />
                      : <span className="text-gray-700 text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      <Pagination
        page={page}
        totalPages={Math.ceil(total / 20)}
        total={total}
        label="orders"
        onPage={setPage}
      />
    </div>
    </>
  );
}
